import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, ChevronUp, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DISCOUNT_SCOPE, EXPIRY_CHOICES, SECTIONS, SELL_SCOPE, expiryHours, hasDiscount, sectionState, toggleScope, validate,
  type CatalogEntry, type FormProblem, type LinkForm, type MappedServerError, type SectionKey, type SectionState,
} from './link-form';
import { LinkSummary, summaryAnnouncement, useLinkSummary, useSummaryFormatters } from './LinkSummary';

/** Three columns only when the dialog itself is at least about 1000 px wide (viewport width is not the test). */
export const WIDE_BODY_PX = 940;

const scopeKey = (s: string) => `scope_${s.replace(/[:-]/g, '_')}`;
const scopeHintKey = (s: string) => `scopeHint_${s.replace(/[:-]/g, '_')}`;
const scopeDescKey = (s: string) => `scopeDesc_${s.replace(/[:-]/g, '_')}`;
const SECTION_KEY: Record<SectionKey, string> = {
  basics: 'secBasics', activities: 'secActivities', discounts: 'secDiscounts', limits: 'secLimits', review: 'secReview',
};

const SELECT_CLASS =
  'flex h-11 w-full rounded-lg border border-surface/12 bg-background px-3 py-2 text-sm shadow-sm sm:h-10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 disabled:cursor-not-allowed disabled:opacity-50';
const INPUT_CLASS = 'h-11 sm:h-10';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export interface CreateLinkDialogProps {
  open: boolean;
  /** Starting values for this opening (empty form, or the prefill of "Create similar"). */
  initialForm: LinkForm;
  /** Activities that could not be copied by "Create similar" (shown as a notice). */
  dropped?: string[];
  catalog: CatalogEntry[];
  catalogLoading: boolean;
  catalogError: boolean;
  onRetryCatalog: () => void;
  locations: { id: string; name: string }[];
  isPending: boolean;
  /** Translated server refusal of the last create attempt (shown inline, role=alert). */
  serverError: MappedServerError | null;
  /** The shop's manager-PIN discount threshold, learned from a server refusal. */
  pinThreshold: number | null;
  onSubmit: (form: LinkForm) => void;
  onClose: () => void;
}

export function CreateLinkDialog(props: CreateLinkDialogProps) {
  const { open, initialForm, dropped = [], catalog, catalogLoading, catalogError, locations, isPending, serverError, pinThreshold, onSubmit, onClose } = props;
  const { t } = useTranslation('mobileLinks');
  const [form, setForm] = useState<LinkForm>(initialForm);
  const startRef = useRef<LinkForm>(initialForm);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [discardOpen, setDiscardOpen] = useState(false);
  const [bar, setBar] = useState(false);
  const [active, setActive] = useState<SectionKey>('basics');
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [wide, setWide] = useState(true);
  const bannerRef = useRef<HTMLDivElement>(null);

  // A new opening starts from its own initial values with nothing touched.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(initialForm);
      startRef.current = initialForm;
      setAttempted(false);
      setTouched(new Set());
      setDiscardOpen(false);
      setBar(false);
      setActive('basics');
      window.setTimeout(() => document.getElementById('link-name')?.focus(), 250);
    }
    wasOpen.current = open;
  }, [open, initialForm]);

  // Layout follows the dialog's own width.
  useEffect(() => {
    if (!bodyEl) return undefined;
    const measure = () => setWide(bodyEl.clientWidth >= WIDE_BODY_PX);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(bodyEl);
    return () => ro.disconnect();
  }, [bodyEl]);

  // Rail follows the section in view.
  useEffect(() => {
    const scroller = bodyEl?.closest<HTMLElement>('.p-dialog-content');
    if (!scroller) return undefined;
    const onScroll = () => {
      const top = scroller.getBoundingClientRect().top;
      let current: SectionKey = 'basics';
      for (const s of SECTIONS) {
        const el = document.getElementById(`sec-${s}`);
        if (el && el.getBoundingClientRect().top - top <= 110) current = s;
      }
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) current = 'review';
      setActive(current);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [bodyEl]);

  const problems = useMemo(() => validate(form, { pinThreshold }), [form, pinThreshold]);
  const shown = useMemo(() => (attempted ? problems : problems.filter((p) => touched.has(p.field))), [attempted, problems, touched]);
  const errOf = (field: string): FormProblem | undefined => shown.find((p) => p.field === field);

  const dirty = JSON.stringify(form) !== JSON.stringify(startRef.current);
  const hours = expiryHours(form);
  const expiresAt = useMemo(() => (hours ? new Date(Date.now() + hours * 3_600_000) : null), [hours]);
  const locationName = form.locationId ? locations.find((l) => l.id === form.locationId)?.name ?? null : null;
  const fmt = useSummaryFormatters();
  const summary = useLinkSummary(form, catalog, expiresAt, locationName);
  const announce = useDebounced(summaryAnnouncement(t, summary), 900);

  const set = (patch: Partial<LinkForm>) => setForm((f) => ({ ...f, ...patch }));
  const touch = (field: string) => setTouched((s) => (s.has(field) ? s : new Set(s).add(field)));
  const focusField = useCallback((field: string) => {
    const el = document.getElementById(field);
    if (!el) return;
    el.focus();
    el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, []);

  const trySubmit = () => {
    setAttempted(true);
    if (problems.length > 0) {
      focusField(problems[0].field);
      return;
    }
    if (isPending || catalogLoading || catalogError) return;
    onSubmit(form);
  };

  // A server refusal is shown in the banner and, when it belongs to a field, moves focus there.
  useEffect(() => {
    if (!serverError || !open) return;
    bannerRef.current?.scrollIntoView({ block: 'nearest' });
    if (serverError.field) {
      setAttempted(true);
      document.getElementById(serverError.field)?.focus();
    }
  }, [serverError, open]);

  const requestClose = () => {
    if (isPending) return;
    if (dirty) setDiscardOpen(true);
    else onClose();
  };

  const onBodyKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      trySubmit();
      return;
    }
    // Enter in a field never creates the link (the link is shown once and uses a link slot).
    if ((e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
  };

  const goTo = (key: SectionKey) => {
    const sec = document.getElementById(`sec-${key}`);
    sec?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    document.getElementById(`section-${key}`)?.focus({ preventScroll: true });
    setActive(key);
  };
  const onRailKey = (e: KeyboardEvent<HTMLElement>) => {
    const next = ['ArrowDown', 'ArrowRight'];
    const prev = ['ArrowUp', 'ArrowLeft'];
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-rail]'));
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    if (next.includes(e.key)) { e.preventDefault(); items[(i + 1) % items.length].focus(); }
    else if (prev.includes(e.key)) { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
  };

  const states = useMemo(
    () => Object.fromEntries(SECTIONS.map((s) => [s, sectionState(s, form, problems, attempted)])) as Record<SectionKey, SectionState>,
    [form, problems, attempted],
  );

  const discEntry = catalog.find((c) => c.key === DISCOUNT_SCOPE);
  const holdsDiscount = !!discEntry?.grantable;
  const hasSell = form.scopes.includes(SELL_SCOPE);
  const discOn = hasDiscount(form);
  const discDisabled = catalogLoading || !holdsDiscount || (!hasSell && !discOn);
  const hasRestaurant = catalog.some((c) => c.key.startsWith('restaurant:'));

  const stateIcon = (s: SectionState) =>
    s === 'complete' ? <Check className="h-3.5 w-3.5 text-success-text" aria-hidden /> : s === 'problem' ? <AlertTriangle className="h-3.5 w-3.5 text-destructive-text" aria-hidden /> : <Circle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />;
  const stateText = (s: SectionState) => t(s === 'complete' ? 'stateComplete' : s === 'problem' ? 'stateProblem' : 'stateNotStarted');

  const rail = (
    <nav
      aria-label={t('railLabel')}
      onKeyDown={onRailKey}
      className={cn(
        wide
          ? 'sticky top-0 flex flex-col gap-1 self-start'
          : 'scrollbar-none sticky top-0 z-10 -mx-1 flex gap-1 overflow-x-auto border-b bg-card px-1 pb-2 pt-1',
      )}
    >
      {SECTIONS.map((s, i) => (
        <button
          key={s}
          type="button"
          data-rail
          aria-current={active === s ? 'true' : undefined}
          onClick={() => goTo(s)}
          className={cn(
            'flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-start transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active === s ? 'bg-primary/10 text-primary-text ring-1 ring-inset ring-primary/20' : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
          )}
        >
          {stateIcon(states[s])}
          <span className="min-w-0">
            <span className="block text-sm font-medium">{i + 1}. {t(SECTION_KEY[s])}</span>
            <span className="block text-[11px] leading-tight opacity-80">{stateText(states[s])}</span>
          </span>
        </button>
      ))}
    </nav>
  );

  const summaryPanel = (
    <LinkSummary summary={summary} />
  );

  const problemList = attempted && problems.length > 0 && (
    <ul className="space-y-1 text-sm text-destructive-text">
      {problems.map((p) => (
        <li key={`${p.field}-${p.key}`}>
          <button type="button" className="text-start underline underline-offset-2" onClick={() => focusField(p.field)}>
            {t(p.key, p.params)}
          </button>
        </li>
      ))}
    </ul>
  );

  const fieldMsg = (field: string) => {
    const e = errOf(field);
    return e ? (
      <p id={`${field}-err`} className="text-xs text-destructive-text">{t(e.key, e.params)}</p>
    ) : null;
  };
  const fieldProps = (field: string, describedBy?: string) => {
    const e = errOf(field);
    return {
      id: field,
      onBlur: () => touch(field),
      'aria-invalid': e ? (true as const) : undefined,
      'aria-describedby': [e ? `${field}-err` : '', describedBy ?? ''].filter(Boolean).join(' ') || undefined,
    };
  };

  const heading = (s: SectionKey, children?: ReactNode) => (
    <div className="mb-3">
      <h3 id={`section-${s}`} tabIndex={-1} className="text-base font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {SECTIONS.indexOf(s) + 1}. {t(SECTION_KEY[s])}
      </h3>
      {children}
    </div>
  );

  const body = (
    <div ref={setBodyEl} onKeyDown={onBodyKeyDown} className="min-w-0">
      <div className="sr-only" aria-live="polite" role="status">{announce}</div>
      {serverError && (
        <div ref={bannerRef} role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
          {t(serverError.key, serverError.params)}
        </div>
      )}
      {catalogError && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
          <span className="min-w-0 flex-1">{t('catalogError')}</span>
          <Button type="button" variant="outline" size="sm" onClick={props.onRetryCatalog}>{t('catalogRetry')}</Button>
        </div>
      )}
      {dropped.length > 0 && (
        <div role="status" className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          {t('similarDropped', { list: dropped.map((s) => t(scopeKey(s), s)).join(', ') })}
        </div>
      )}

      <div className={wide ? 'grid grid-cols-[180px_minmax(0,1fr)_300px] items-start gap-6' : 'flex flex-col gap-4'}>
        {rail}

        <div className="min-w-0 space-y-8">
          {/* 1 Basics */}
          <section id="sec-basics" aria-labelledby="section-basics" className="scroll-mt-16">
            {heading('basics')}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="link-name">{t('fieldName')} *</Label>
                <Input
                  {...fieldProps('link-name')}
                  ref={(el) => el?.setAttribute('autofocus', '')}
                  className={INPUT_CLASS}
                  value={form.name}
                  maxLength={80}
                  required
                  placeholder={t('fieldNamePlaceholder')}
                  onChange={(e) => set({ name: e.target.value })}
                />
                {fieldMsg('link-name')}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="link-expiry">{t('fieldExpiry')}</Label>
                  <select id="link-expiry" className={SELECT_CLASS} value={form.expiry} onChange={(e) => set({ expiry: e.target.value })}>
                    {EXPIRY_CHOICES.map((h) => <option key={h} value={h}>{t(`expiry_${h}`)}</option>)}
                    <option value="other">{t('expiry_other')}</option>
                  </select>
                  {form.expiry === 'other' && (
                    <div className="space-y-1.5 pt-1">
                      <Label htmlFor="link-expiry-other">{t('fieldExpiryOther')}</Label>
                      <Input {...fieldProps('link-expiry-other')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="numeric" min={1} max={720} value={form.expiryOther} onChange={(e) => set({ expiryOther: e.target.value })} />
                      {fieldMsg('link-expiry-other')}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="link-location">{t('fieldLocation')}</Label>
                  <select id="link-location" className={SELECT_CLASS} value={form.locationId} onChange={(e) => set({ locationId: e.target.value })}>
                    <option value="">{t('locationDefault')}</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5 sm:max-w-xs">
                <Label htmlFor="link-max">{t('fieldMaxUses')}</Label>
                <Input {...fieldProps('link-max', 'link-max-hint')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="numeric" min={1} max={1000} value={form.maxUses} onChange={(e) => set({ maxUses: e.target.value })} />
                <p id="link-max-hint" className="text-xs text-muted-foreground">{t('fieldMaxUsesHint')}</p>
                {fieldMsg('link-max')}
              </div>
              <p className="rounded-lg bg-muted p-2 text-xs text-muted-foreground">{t('oneAtATime')}</p>
            </div>
          </section>

          {/* 2 Activities */}
          <section id="sec-activities" aria-labelledby="section-activities" className="scroll-mt-16">
            {heading('activities', <p className="mt-1 text-xs text-muted-foreground">{t('activitiesHint')}</p>)}
            {catalogLoading ? (
              <div className="space-y-2" aria-busy="true">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <div className="space-y-2" role="group" aria-labelledby="section-activities">
                {catalog.filter((c) => c.key !== DISCOUNT_SCOPE).map((c) => {
                  const id = `scope-${c.key.replace(/[:-]/g, '_')}`;
                  const label = t(scopeKey(c.key), c.label);
                  const checked = form.scopes.includes(c.key);
                  return (
                    <label
                      key={c.key}
                      htmlFor={id}
                      className={cn(
                        'flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm',
                        checked && 'border-primary/40 bg-primary/5',
                        !c.grantable && 'cursor-not-allowed opacity-80',
                      )}
                    >
                      <Checkbox
                        id={id}
                        className="mt-0.5"
                        checked={checked}
                        disabled={!c.grantable}
                        onChange={() => set({ scopes: toggleScope(form.scopes, c.key) })}
                        aria-describedby={`${id}-desc`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{label}</span>
                        <span id={`${id}-desc`} className="mt-0.5 block text-xs text-muted-foreground">
                          {t(scopeDescKey(c.key), { defaultValue: '' })}
                          {c.key.startsWith('restaurant:') && c.grantable && <span className="block">{t(scopeHintKey(c.key))}</span>}
                        </span>
                        <span className="mt-1.5 block">
                          {c.grantable ? <Badge variant="success">{t('canGive')}</Badge> : <Badge variant="outline">{t('cannotGive')}</Badge>}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {hasRestaurant && <p className="rounded-lg bg-muted p-2 text-xs text-muted-foreground">{t('restaurantHint')}</p>}
                {errOf('section-activities') && <p id="section-activities-err" className="text-xs text-destructive-text">{t(errOf('section-activities')!.key)}</p>}
              </div>
            )}
          </section>

          {/* 3 Discounts */}
          <section id="sec-discounts" aria-labelledby="section-discounts" className="scroll-mt-16">
            {heading('discounts')}
            <div className="space-y-3">
              <div className="flex min-h-[44px] items-start gap-3">
                <Switch
                  id="disc-switch"
                  aria-label={t('discAllow')}
                  checked={discOn}
                  disabled={discDisabled}
                  onCheckedChange={(on) =>
                    set({
                      scopes: on ? [...form.scopes, DISCOUNT_SCOPE] : form.scopes.filter((s) => s !== DISCOUNT_SCOPE),
                      maxDiscountPercent: on && !form.maxDiscountPercent ? '10' : form.maxDiscountPercent,
                    })
                  }
                />
                <div className="min-w-0 flex-1 text-sm">
                  <label htmlFor="disc-switch" className="block font-medium">{t('discAllow')}</label>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t('discAllowHint')}</p>
                  {!catalogLoading && !holdsDiscount && <p className="mt-1 text-xs text-warning-text">{t('discNotHeld')}</p>}
                  {!catalogLoading && holdsDiscount && !hasSell && !discOn && <p className="mt-1 text-xs text-warning-text">{t('discNeedsSell')}</p>}
                  {!catalogLoading && holdsDiscount && <p className="mt-1 text-xs text-muted-foreground">{t('discOwnLimit')}</p>}
                  {fieldMsg('disc-switch')}
                </div>
              </div>
              {discOn && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="disc-percent">{t('discMaxPercent')} *</Label>
                    <Input {...fieldProps('disc-percent', 'disc-percent-hint')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="decimal" min={0.01} max={100} step={0.01} value={form.maxDiscountPercent} onChange={(e) => set({ maxDiscountPercent: e.target.value })} />
                    <p id="disc-percent-hint" className="text-xs text-muted-foreground">{t('discMaxPercentHelp')}</p>
                    {fieldMsg('disc-percent')}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="disc-amount">{t('discMaxAmount')}</Label>
                    <Input {...fieldProps('disc-amount', 'disc-amount-hint')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="decimal" min={0.01} step={0.01} value={form.maxDiscountAmount} onChange={(e) => set({ maxDiscountAmount: e.target.value })} />
                    <p id="disc-amount-hint" className="text-xs text-muted-foreground">{t('discMaxAmountHelp')}</p>
                    {fieldMsg('disc-amount')}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 4 Limits and safety */}
          <section id="sec-limits" aria-labelledby="section-limits" className="scroll-mt-16">
            {heading('limits', <p className="mt-1 text-xs text-muted-foreground">{t('capsHint')}</p>)}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cap-amount">{t('capAmount')}</Label>
                  <Input {...fieldProps('cap-amount')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="decimal" min={0.01} step={0.01} value={form.maxSaleAmount} onChange={(e) => set({ maxSaleAmount: e.target.value })} />
                  {fieldMsg('cap-amount')}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cap-hour">{t('capPerHour')}</Label>
                  <Input {...fieldProps('cap-hour')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="numeric" min={1} value={form.maxSalesPerHour} onChange={(e) => set({ maxSalesPerHour: e.target.value })} />
                  {fieldMsg('cap-hour')}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cap-day">{t('capDaily')}</Label>
                  <Input {...fieldProps('cap-day')} className={INPUT_CLASS} dir="ltr" type="number" inputMode="decimal" min={0.01} step={0.01} value={form.dailyTotalCap} onChange={(e) => set({ dailyTotalCap: e.target.value })} />
                  {fieldMsg('cap-day')}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cap-from">{t('allowedFrom')}</Label>
                  <Input {...fieldProps('cap-from')} className={INPUT_CLASS} dir="ltr" type="time" value={form.allowedFrom} onChange={(e) => set({ allowedFrom: e.target.value })} />
                  {fieldMsg('cap-from')}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cap-to">{t('allowedTo')}</Label>
                  <Input {...fieldProps('cap-to')} className={INPUT_CLASS} dir="ltr" type="time" value={form.allowedTo} onChange={(e) => set({ allowedTo: e.target.value })} />
                  {fieldMsg('cap-to')}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cap-cidr">{t('allowedNetworks')}</Label>
                <Input {...fieldProps('cap-cidr')} className={INPUT_CLASS} dir="ltr" value={form.allowedCidrs} placeholder="203.0.113.0/24, 198.51.100.7" onChange={(e) => set({ allowedCidrs: e.target.value })} />
                {fieldMsg('cap-cidr')}
              </div>
              <label htmlFor="cap-bind" className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm">
                <Checkbox id="cap-bind" className="mt-0.5" checked={form.bindDevice} onChange={() => set({ bindDevice: !form.bindDevice })} aria-describedby="cap-bind-hint" />
                <span>
                  <span className="block font-medium">{t('bindDevice')}</span>
                  <span id="cap-bind-hint" className="block text-xs text-muted-foreground">{t('bindDeviceHint')}</span>
                </span>
              </label>
            </div>
          </section>

          {/* 5 Review */}
          <section id="sec-review" aria-labelledby="section-review" className="scroll-mt-16 pb-2">
            {heading('review')}
            <div className="space-y-3 text-sm">
              <div aria-live="polite" role="status">
                {problems.length === 0 ? (
                  <p className="font-medium text-success-text">{t('reviewOk')}</p>
                ) : attempted ? (
                  <div className="space-y-2">
                    <p className="font-medium text-destructive-text">{t('problemsCount', { count: problems.length })}</p>
                    {problemList}
                  </div>
                ) : null}
              </div>
              {expiresAt && <p>{t('reviewExpires', { date: fmt.date(expiresAt) })}</p>}
              <p className="text-muted-foreground">{t('reviewOnce')}</p>
            </div>
          </section>
        </div>

        {wide && (
          <aside aria-labelledby="sum-heading" className="sticky top-0 self-start rounded-lg border bg-card p-4">
            <h3 id="sum-heading" className="mb-3 text-sm font-semibold">{t('summaryTitle')}</h3>
            {summaryPanel}
            {attempted && problems.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <p className="mb-1 text-sm font-medium text-destructive-text">{t('problemsCount', { count: problems.length })}</p>
                {problemList}
              </div>
            )}
          </aside>
        )}
      </div>

      {!wide && (
        <aside aria-labelledby="sum-heading" className="sticky bottom-0 z-10 -mx-1 mt-4 border-t bg-card px-1 pt-1">
          <h3 id="sum-heading" className="sr-only">{t('summaryTitle')}</h3>
          <button
            type="button"
            aria-expanded={bar}
            aria-controls="sum-panel"
            aria-label={`${t('summaryBarToggle')}. ${t('summaryBar', { can: summary.can.length, cannot: summary.cannot.length, limits: summary.limits.length })}`}
            onClick={() => setBar((b) => !b)}
            className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-2 text-start text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span>{t('summaryBar', { can: summary.can.length, cannot: summary.cannot.length, limits: summary.limits.length })}</span>
            <ChevronUp className={cn('h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none', bar && 'rotate-180')} aria-hidden />
          </button>
          <div id="sum-panel" hidden={!bar} className="max-h-[45vh] overflow-y-auto px-2 pb-3 pt-1">
            {summaryPanel}
          </div>
        </aside>
      )}
    </div>
  );

  const chip =
    attempted && problems.length > 0 ? (
      <button
        type="button"
        onClick={() => focusField(problems[0].field)}
        aria-label={`${t('problemsCount', { count: problems.length })}. ${t('problemsGo')}`}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 px-3 text-xs font-medium text-destructive-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0 sm:py-1"
      >
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        {t('problemsCount', { count: problems.length })}
      </button>
    ) : null;

  return (
    <>
      <FormDialog
        open={open}
        size="large"
        title={t('createTitle')}
        subtitle={t('newSubtitle')}
        submitLabel={t('createSubmit')}
        isPending={isPending}
        submitDisabled={catalogLoading || catalogError}
        footerLeading={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {chip}
            <span className="hidden sm:inline">{t('kbdHint')}</span>
          </div>
        }
        onSubmit={(e) => { e.preventDefault(); trySubmit(); }}
        onClose={requestClose}
      >
        {body}
      </FormDialog>
      <ConfirmDialog
        open={discardOpen}
        variant="warning"
        title={t('discardTitle')}
        message={t('discardBody')}
        confirmLabel={t('discardConfirm')}
        cancelLabel={t('discardKeep')}
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}

